"use client";

import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FaCheck, FaXmark, FaMinus } from "react-icons/fa6";
import type { PlanPricingByKey } from "@/lib/pricing/catalog";

type Support = "yes" | "partial" | "no";

/** Who the visitor is buying for: the table, its arguments and its prices follow. */
type Mode = "team" | "individual";

const MODES: Mode[] = ["team", "individual"];

/** Team first: the offer is sold to organisations, the individual view is the other tab. */
const DEFAULT_MODE: Mode = "team";

const COMPETITORS = ["notion", "trello", "todoist"] as const;

type ComparisonRow = {
  /** Copy in `compare.<mode>.row.<id>`. */
  id: string;
  flots: Support;
  notion: Support;
  trello: Support;
  todoist: Support;
};

/**
 * The arguments, per mode. The criteria differ on purpose: a team cares about the same-day
 * start and meeting notes turning into tasks; an individual about getting going alone.
 */
const ROWS: Record<Mode, ComparisonRow[]> = {
  team: [
    { id: "centralization", flots: "yes", notion: "yes", trello: "no", todoist: "no" },
    { id: "notesToTasks", flots: "yes", notion: "partial", trello: "no", todoist: "partial" },
    { id: "noSetup", flots: "yes", notion: "no", trello: "yes", todoist: "yes" },
    { id: "frenchSupport", flots: "yes", notion: "partial", trello: "partial", todoist: "partial" },
    { id: "euHosting", flots: "yes", notion: "partial", trello: "no", todoist: "no" },
  ],
  individual: [
    { id: "centralization", flots: "yes", notion: "yes", trello: "no", todoist: "no" },
    { id: "notesToTasks", flots: "yes", notion: "partial", trello: "no", todoist: "partial" },
    { id: "noSetup", flots: "yes", notion: "no", trello: "yes", todoist: "yes" },
    { id: "frenchSupport", flots: "yes", notion: "partial", trello: "partial", todoist: "partial" },
    { id: "euHosting", flots: "yes", notion: "no", trello: "no", todoist: "no" },
  ],
};

/** The Flots plan each mode quotes, read from the catalogue (never written here). */
const FLOTS_PLAN: Record<Mode, "team" | "individual"> = {
  team: "team",
  individual: "individual",
};

function SupportIcon({ value }: { value: Support }) {
  const intl = useIntl();
  const label = intl.formatMessage({ id: `compare.support.${value}` });

  const styles: Record<Support, string> = {
    yes: "bg-green-main text-white",
    partial: "bg-amber-ulight text-amber-main",
    no: "bg-gray-light text-gray-sec",
  };

  const Icon = value === "yes" ? FaCheck : value === "partial" ? FaMinus : FaXmark;

  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-[9px] sm:text-[11px] ${styles[value]}`}
    >
      <Icon aria-hidden="true" />
    </span>
  );
}

/** Team / Individual switch, as a radio group (arrow keys move, Tab leaves). */
function ModeSwitch({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  const intl = useIntl();

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const next = MODES[(MODES.indexOf(mode) + 1) % MODES.length];
    onChange(next);
    (e.currentTarget.parentElement?.querySelector(`[data-mode="${next}"]`) as HTMLElement | null)?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={intl.formatMessage({ id: "compare.switch.label" })}
      className="mx-auto mb-8 inline-flex rounded-full bg-surface p-1 shadow-sm ring-1 ring-blue-light"
    >
      {MODES.map((m) => {
        const on = m === mode;
        return (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={on}
            data-mode={m}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(m)}
            onKeyDown={onKeyDown}
            className={`min-h-11 cursor-pointer rounded-full px-5 text-sm font-semibold transition-colors duration-200 sm:min-h-10 sm:px-6 ${
              on ? "bg-blue-main text-white shadow-sm" : "text-gray-main hover:text-title-blue"
            }`}
          >
            <FormattedMessage id={`compare.switch.${m}`} />
          </button>
        );
      })}
    </div>
  );
}

export default function ComparisonSection({
  pricing,
}: {
  pricing: PlanPricingByKey;
}) {
  const intl = useIntl();
  const [mode, setMode] = useState<Mode>(DEFAULT_MODE);
  const t = (id: string) => `compare.${mode}.${id}`;

  // The row compares monthly plans, so it quotes the Flots plan's monthly price per seat,
  // excluding tax — the basis the competitor figures beside it are given on.
  const flotsPlan = pricing[FLOTS_PLAN[mode]];
  const entryPriceCents = flotsPlan?.monthly?.amountCents ?? null;
  const entryPrice =
    entryPriceCents === null
      ? null
      : intl.formatNumber(entryPriceCents / 100, {
          style: "currency",
          currency: flotsPlan?.currency ?? "EUR",
          minimumFractionDigits: entryPriceCents % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        });

  return (
    <section id="comparatif" className="w-full bg-blue-ulight py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block bg-surface text-brand text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-6">
            <FormattedMessage id="compare.badge" />
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-title-blue mb-4 text-balance max-w-3xl mx-auto">
            <FormattedMessage id={t("title")} />
          </h2>
          <p className="text-[17px] text-text-main max-w-2xl mx-auto leading-[1.7] text-pretty">
            <FormattedMessage id={t("subtitle")} />
          </p>
        </div>

        <div className="text-center">
          <ModeSwitch mode={mode} onChange={setMode} />
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-gray-ulight shadow-lg shadow-black/5 overflow-hidden">
          <table className="w-full table-fixed sm:table-auto sm:min-w-140 border-collapse text-center">
            <caption className="sr-only">
              <FormattedMessage id={t("caption")} />
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="w-[26%] sm:w-2/5 text-left px-1.5 sm:px-6 py-5"
                >
                  <span className="sr-only">
                    <FormattedMessage id="compare.criteriaHeader" />
                  </span>
                </th>
                <th
                  scope="col"
                  className="bg-blue-main text-white px-1 sm:px-4 py-5 rounded-t-2xl align-middle"
                >
                  <span className="block text-[11px] sm:text-base font-bold">Flots</span>
                  <span className="block text-[8px] sm:text-xs font-normal text-white leading-tight">
                    <FormattedMessage id={t("plan.flots")} />
                  </span>
                </th>
                {COMPETITORS.map((name) => (
                  <th
                    scope="col"
                    key={name}
                    className="bg-surface px-1 sm:px-4 py-5 text-[11px] sm:text-base font-semibold text-gray-main"
                  >
                    <span className="block capitalize">{name}</span>
                    <span className="block text-[8px] sm:text-xs font-normal leading-tight">
                      <FormattedMessage id={t(`plan.${name}`)} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS[mode].map((row) => (
                <tr key={row.id} className="border-t border-gray-light">
                  <th
                    scope="row"
                    className="text-left px-1.5 sm:px-6 py-3 sm:py-4 text-[11px] sm:text-sm font-semibold text-text-main leading-snug"
                  >
                    <FormattedMessage id={t(`row.${row.id}`)} />
                  </th>
                  <td className="bg-blue-ulight px-1 sm:px-4 py-3 sm:py-4">
                    <SupportIcon value={row.flots} />
                  </td>
                  {COMPETITORS.map((name) => (
                    <td key={name} className="bg-surface px-1 sm:px-4 py-3 sm:py-4">
                      <SupportIcon value={row[name]} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-gray-light">
                <th
                  scope="row"
                  className="text-left px-1.5 sm:px-6 py-4 sm:py-5 text-[11px] sm:text-sm font-semibold text-text-main leading-snug"
                >
                  <FormattedMessage id={t("row.price")} />
                </th>
                <td className="bg-blue-ulight px-1 sm:px-4 py-4 sm:py-5 rounded-b-2xl text-[10px] sm:text-base font-bold text-brand-ink">
                  {entryPrice ?? <FormattedMessage id="compare.price.unavailable" />}
                </td>
                {COMPETITORS.map((name) => (
                  <td key={name} className="bg-surface px-1 sm:px-4 py-4 sm:py-5 text-[10px] sm:text-base text-gray-main">
                    <FormattedMessage id={t(`price.${name}`)} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-main text-center mt-4 max-w-3xl mx-auto">
          <FormattedMessage id={t("footnote")} />
        </p>
      </div>
    </section>
  );
}
